import { CODE_SYSTEMS, LOINC_CODES } from '@liza/fhir-utils';
import type { PlanDefinition, PlanDefinitionAction } from '@medplum/fhirtypes';
import {
  ADA_DERIVED_FROM,
  T2DM_FOCUS_CODINGS,
  ada,
  applicable,
  daysPerWeek,
  every,
  itemKind,
  loincCode,
  medicationAdded,
  patient,
  practitioner,
  taskCategory,
} from './builders';
import { STATIN_ACTIVITY_CANONICAL } from './activities/statinModerateIntensity';
import { insulinsValueSet, statinsValueSet, sulfonylureasValueSet } from '../valueSets/rxNormDrugClasses';

export const T2DM_ROUTINE_MONITORING_URL = 'https://liza.health/fhir/PlanDefinition/t2dm-routine-monitoring';
export const T2DM_ROUTINE_MONITORING_VERSION = '1.0.0';
/** Version-pinned canonical, the value a RequestGroup / CarePlan `instantiatesCanonical` carries. */
export const T2DM_ROUTINE_MONITORING_CANONICAL = `${T2DM_ROUTINE_MONITORING_URL}|${T2DM_ROUTINE_MONITORING_VERSION}`;

/**
 * The PHQ-9 Questionnaire this protocol assigns, version-pinned. Named here as a string because this
 * package cannot import `@liza/medplum-config` (which would be a cycle); medplum-config tests that the
 * canonical resolves to a deployed Questionnaire.
 */
export const PHQ9_QUESTIONNAIRE_CANONICAL = 'https://liza.health/fhir/questionnaire/phq-9|4';

/** The LOINC codes this protocol tracks, named from the shared `LOINC_CODES`. */
export const T2DM_CODES = {
  A1C: LOINC_CODES.HBA1C,
  BLOOD_PRESSURE: LOINC_CODES.BLOOD_PRESSURE_PANEL,
  BODY_WEIGHT: LOINC_CODES.BODY_WEIGHT,
  BLOOD_GLUCOSE: LOINC_CODES.BLOOD_GLUCOSE,
  LDL: LOINC_CODES.LDL_CHOLESTEROL,
  STEPS: LOINC_CODES.STEPS,
  PHQ9_TOTAL_SCORE: LOINC_CODES.PHQ9_TOTAL_SCORE,
} as const;

/**
 * The variables the protocol engine binds before evaluating any condition. Medplum's FHIRPath
 * evaluator takes each variable as a single typed value — a resource, a number, a boolean, a
 * dateTime — not a collection, and has no `age()` or date-minus-duration, so anything derived
 * (age, set membership against a ValueSet) is computed by the engine and bound ready to compare.
 */
export const T2DM_CONTEXT_VARIABLES = [
  { name: '%a1c', description: `Latest Observation with LOINC ${T2DM_CODES.A1C}, from any source; empty when none.` },
  { name: '%ldl', description: `Latest Observation with LOINC ${T2DM_CODES.LDL}, from any source; empty when none.` },
  { name: '%ageYears', description: 'Integer age in years from Patient.birthDate.' },
  {
    name: '%onStatin',
    description: `Boolean: an active MedicationRequest whose ingredient RxCUI is in ${statinsValueSet.url}.`,
  },
  {
    name: '%statinStartedAt',
    description: 'dateTime: authoredOn of the earliest active statin MedicationRequest; empty when not on a statin.',
  },
  {
    name: '%onInsulinOrSulfonylurea',
    description: `Boolean: an active MedicationRequest whose ingredient RxCUI is in ${insulinsValueSet.url} or ${sulfonylureasValueSet.url}.`,
  },
  {
    name: '%footHighRisk',
    description:
      'Boolean: an active Condition of diabetic neuropathy or diabetic foot ulcer, coded in ICD-10-CM or SNOMED CT, or of acquired absence of toe, foot or leg coded in ICD-10-CM.',
  },
] as const;

export type T2dmContextVariable = (typeof T2DM_CONTEXT_VARIABLES)[number]['name'];

// --- building blocks ---------------------------------------------------------------------------------

// --- actions ------------------------------------------------------------------------------------------

/**
 * Two sibling actions with complementary conditions rather than one action with two timings: each
 * materializes as its own Task, so a switch between them is a cancel-and-create the engine can
 * reconcile, exactly as the editor's reschedule is.
 */
const a1cQuarterly: PlanDefinitionAction = {
  id: 'a1c-quarterly',
  title: 'Hemoglobin A1C',
  description: 'A1C every 3 months while above goal, or until the first result is on record.',
  code: loincCode(T2DM_CODES.A1C),
  goalId: ['goal-a1c'],
  participant: patient,
  extension: [itemKind('measurement'), taskCategory('measurement')],
  condition: applicable(
    '%a1c.empty() or %a1c.value.value >= 7',
    'No A1C on record, or the latest A1C is at or above 7%',
  ),
  timingTiming: every(3, 'mo'),
  documentation: ada('Recommendation 6.2'),
};

const a1cSemiannual: PlanDefinitionAction = {
  id: 'a1c-semiannual',
  title: 'Hemoglobin A1C',
  description: 'A1C at least twice a year while at goal.',
  code: loincCode(T2DM_CODES.A1C),
  goalId: ['goal-a1c'],
  participant: patient,
  extension: [itemKind('measurement'), taskCategory('measurement')],
  condition: applicable('%a1c.exists() and %a1c.value.value < 7', 'The latest A1C is below 7%'),
  timingTiming: every(6, 'mo'),
  documentation: ada('Recommendation 6.1'),
};

const bloodPressureHome: PlanDefinitionAction = {
  id: 'bp-home',
  title: 'Home blood pressure',
  description: 'Home blood pressure monitoring on three days a week.',
  code: loincCode(T2DM_CODES.BLOOD_PRESSURE),
  participant: patient,
  extension: [itemKind('measurement'), taskCategory('measurement')],
  timingTiming: daysPerWeek(3),
  documentation: ada('Recommendations 10.1 and 10.2'),
};

const weightWeekly: PlanDefinitionAction = {
  id: 'weight-weekly',
  title: 'Body weight',
  description: 'Weekly weight, toward a 5–7% reduction from the starting weight.',
  code: loincCode(T2DM_CODES.BODY_WEIGHT),
  participant: patient,
  extension: [itemKind('measurement'), taskCategory('measurement')],
  timingTiming: every(1, 'wk'),
  documentation: ada('Recommendations 3.3 and 8.5'),
};

const glucoseDaily: PlanDefinitionAction = {
  id: 'glucose-daily',
  title: 'Blood glucose',
  description: 'Daily glucose check while on insulin or an agent that can cause hypoglycemia.',
  code: loincCode(T2DM_CODES.BLOOD_GLUCOSE),
  participant: patient,
  extension: [itemKind('measurement'), taskCategory('measurement')],
  trigger: medicationAdded([insulinsValueSet.url!, sulfonylureasValueSet.url!]),
  condition: applicable('%onInsulinOrSulfonylurea', 'On insulin or a sulfonylurea'),
  timingTiming: every(1, 'd'),
  documentation: ada('Recommendation 7.11'),
};

const phq9Annual: PlanDefinitionAction = {
  id: 'phq9-annual',
  title: 'PHQ-9 depression screening',
  description: 'Depression screening at least annually.',
  code: loincCode(T2DM_CODES.PHQ9_TOTAL_SCORE),
  definitionCanonical: PHQ9_QUESTIONNAIRE_CANONICAL,
  participant: patient,
  extension: [itemKind('survey'), taskCategory('measurement')],
  timingTiming: every(1, 'a'),
  documentation: ada('Recommendation 5.48'),
};

const stepsDaily: PlanDefinitionAction = {
  id: 'steps-daily',
  title: 'Daily steps',
  description: 'Daily steps as the tracked proxy for 150+ minutes of moderate activity a week.',
  code: loincCode(T2DM_CODES.STEPS),
  goalId: ['goal-steps'],
  participant: patient,
  extension: [itemKind('measurement'), taskCategory('movement')],
  timingTiming: every(1, 'd'),
  documentation: ada('Recommendation 5.36'),
};

const resistanceTraining: PlanDefinitionAction = {
  id: 'resistance-training',
  title: 'Resistance or strength training',
  description: 'Two sessions a week, on non-consecutive days.',
  participant: patient,
  extension: [itemKind('lifestyle'), taskCategory('movement')],
  timingTiming: daysPerWeek(2),
  documentation: ada('Recommendation 5.37'),
};

const lipidsAfterStatin: PlanDefinitionAction = {
  id: 'lipids-post-statin',
  title: 'Lipid panel after starting a statin',
  description: 'LDL 4–12 weeks after statin initiation or a dose change; due at 8 weeks.',
  code: loincCode(T2DM_CODES.LDL),
  participant: practitioner,
  extension: [itemKind('task'), taskCategory('clinical')],
  trigger: medicationAdded([statinsValueSet.url!]),
  condition: applicable(
    '%statinStartedAt.exists() and (%ldl.empty() or %ldl.effective < %statinStartedAt)',
    'On a statin with no LDL result since it was started',
  ),
  timingDuration: { value: 8, unit: 'weeks', system: CODE_SYSTEMS.UCUM, code: 'wk' },
  documentation: ada('Recommendation 10.17'),
};

const footExamAnnual: PlanDefinitionAction = {
  id: 'foot-exam-annual',
  title: 'Comprehensive foot exam',
  description: 'Annual comprehensive foot exam for a patient without high-risk feet.',
  participant: practitioner,
  extension: [itemKind('task'), taskCategory('clinical')],
  condition: applicable('%footHighRisk.not()', 'No sensory loss, prior ulcer or amputation on record'),
  timingTiming: every(1, 'a'),
  documentation: ada('Table 4.1'),
};

/** No timing: the engine raises it again after each completion, standing in for "every visit". */
const footExamEveryVisit: PlanDefinitionAction = {
  id: 'foot-exam-every-visit',
  title: 'Comprehensive foot exam',
  description: 'Foot exam at every visit for a patient with sensory loss, a prior ulcer or an amputation.',
  participant: practitioner,
  extension: [itemKind('task'), taskCategory('clinical')],
  condition: applicable('%footHighRisk', 'Sensory loss, prior ulcer or amputation on record'),
  documentation: ada('Table 4.1'),
};

/**
 * Always a suggested action for a clinician to accept, never an item: starting a therapy is a human
 * decision. `definitionCanonical` names the ActivityDefinition that says what accepting proposes.
 */
const statinRecommendation: PlanDefinitionAction = {
  id: 'statin-recommendation',
  title: 'Consider moderate-intensity statin therapy',
  description: 'Adults 40–75 with diabetes and no statin on their medication list.',
  definitionCanonical: STATIN_ACTIVITY_CANONICAL,
  participant: practitioner,
  extension: [itemKind('suggestion')],
  condition: applicable('%ageYears >= 40 and %ageYears <= 75 and %onStatin.not()', 'Aged 40–75 and not on a statin'),
  documentation: ada('Recommendation 10.18'),
};

// --- the protocol -------------------------------------------------------------------------------------

export const t2dmRoutineMonitoring: PlanDefinition = {
  resourceType: 'PlanDefinition',
  url: T2DM_ROUTINE_MONITORING_URL,
  version: T2DM_ROUTINE_MONITORING_VERSION,
  identifier: [{ system: 'urn:ietf:rfc:3986', value: T2DM_ROUTINE_MONITORING_URL }],
  name: 't2dm-routine-monitoring',
  title: 'Type 2 diabetes — routine monitoring',
  status: 'active',
  experimental: true,
  type: {
    coding: [
      {
        system: 'http://terminology.hl7.org/CodeSystem/plan-definition-type',
        code: 'clinical-protocol',
        display: 'Clinical Protocol',
      },
    ],
  },
  description:
    'Routine monitoring for an adult with type 2 diabetes, from the ADA Standards of Care in Diabetes—2026: glycemic and cardiometabolic measurements, annual screening, activity targets, and the care-team checks that follow from them.',
  useContext: [
    {
      code: { system: 'http://terminology.hl7.org/CodeSystem/usage-context-type', code: 'focus' },
      // Both systems: a diagnosis reaches the chart in whichever its source used, and matching one
      // only makes the protocol invisible on a chart that plainly has the condition (#10129).
      valueCodeableConcept: { coding: [...T2DM_FOCUS_CODINGS] },
    },
  ],
  relatedArtifact: [ADA_DERIVED_FROM],
  goal: [
    {
      id: 'goal-a1c',
      description: { text: 'A1C below 7%' },
      target: [
        {
          measure: loincCode(T2DM_CODES.A1C)[0],
          detailQuantity: { value: 7, comparator: '<', unit: '%', system: CODE_SYSTEMS.UCUM, code: '%' },
        },
      ],
      documentation: ada('Recommendation 6.5a'),
    },
    {
      id: 'goal-steps',
      description: { text: 'At least 10,000 steps a day' },
      target: [
        {
          measure: loincCode(T2DM_CODES.STEPS)[0],
          detailQuantity: { value: 10000, comparator: '>=', unit: 'steps/day' },
        },
      ],
    },
  ],
  // One group ⇒ one care-plan topic. Nested groups are not fanned out into several topics.
  action: [
    {
      id: 'diabetes-management',
      title: 'Diabetes management',
      description: 'Everything the protocol asks of the patient and the care team, on one topic.',
      action: [
        a1cQuarterly,
        a1cSemiannual,
        bloodPressureHome,
        weightWeekly,
        glucoseDaily,
        phq9Annual,
        stepsDaily,
        resistanceTraining,
        lipidsAfterStatin,
        footExamAnnual,
        footExamEveryVisit,
        statinRecommendation,
      ],
    },
  ],
};
